import { useEffect, useRef } from 'react';

import { AlineTransforms } from '@aline/common';
import { useEditorSuggestionState } from 'src/editor/editor-suggestion-state-registry';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useDocContentChangedCount, useMaybeEditor } from 'src/layout-engine';

import { getSuggestionUnitRange } from '../suggestion-units';

/**
 * On each editor content tick, find the first suggestion unit (they are
 * already scoped to the current user) and select+scroll to its range — the
 * exact marker range for in-paragraph edits, the paragraph span of the
 * group's edits for block suggestions. The ref ensures we only scroll once
 * per mount (including after a page reload).
 */
export function useAutoScrollToFirstAISuggestion(): void {
  const editor = useMaybeEditor();
  const currentUserId = useCurrentUserId();
  const hasAutoScrolledRef = useRef(false);
  const { docContentChangedCount } = useDocContentChangedCount();
  const { setSelectedSuggestionGroupId, suggestionUnits } = useEditorSuggestionState();
  const windowHeight = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const scrollOffset = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  useEffect(() => {
    if (hasAutoScrolledRef.current) return;
    if (!editor || !currentUserId || !docContentChangedCount) return;

    const unit = suggestionUnits[0];
    if (!unit) return;

    const range = getSuggestionUnitRange(editor, unit);
    if (!range) return;

    hasAutoScrolledRef.current = true;
    setSelectedSuggestionGroupId(unit.groupId);
    AlineTransforms.select(editor, range);
    ClientTransforms.scrollToLocation(editor, {
      cMLocation: range,
      rWHeightPx: windowHeight,
      rWOffsetPx: scrollOffset,
      select: true,
    });
  }, [
    editor,
    currentUserId,
    docContentChangedCount,
    setSelectedSuggestionGroupId,
    suggestionUnits,
    windowHeight,
    scrollOffset,
  ]);
}
